// useTeams — the teams' read surface (ADR-083): the list on the list-read kit
// (the two arms of visibility are the SERVER's — the query carries only the
// lenses: the typed search, the Mine chip, the status, the order, the page),
// its count (the phone drawer's one question), the team page, the small list of
// names a picker asks whole (ADR-080 §3), and a team's responsibility chains.
// Project-scoped server state: keys are `['teams', projectId, …]` so a
// mutation's invalidate of the prefix refreshes the list, the page and the
// names together (FRONTEND-PATTERNS §Mutations + invalidation). The Teams page
// is not live (a team changes by a hand on this page, never from the field).

import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { TeamNamesQuery, TeamsListQuery } from '@ems-app/schemas/teams/requests';
import type { TeamDetail, TeamNamesResponse, TeamsListResponse } from '@ems-app/schemas/teams/responses';
import { apiClient } from '../lib/apiClient.js';
import type { SmallSource } from './useOptions.js';

/** The view-state the list query travels on — the wire's own query type (ADR-009). */
export type TeamsListParams = Omit<Partial<TeamsListQuery>, 'limit' | 'offset'> & { limit: number; offset: number };

export const teamsKeys = {
  /** List key — also the invalidation PREFIX (covers the page, the names, the detail). */
  all: (projectId: string) => ['teams', projectId] as const,
  list: (projectId: string, params: TeamsListParams) => ['teams', projectId, 'list', params] as const,
  names: (projectId: string, rule: TeamNamesQuery['rule']) => ['teams', projectId, 'names', rule] as const,
  detail: (projectId: string, teamId: string) => ['teams', projectId, teamId] as const,
};

function toWireParams(p: TeamsListParams): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = { limit: p.limit, offset: p.offset };
  if (p.q) out.q = p.q;
  if (p.mine) out.mine = true;
  if (p.status && p.status.length > 0) out.status = p.status.join(',');
  if (p.sort_by) out.sort_by = p.sort_by;
  if (p.sort_dir) out.sort_dir = p.sort_dir;
  return out;
}

export function useTeamsList(projectId: string, params: TeamsListParams): UseQueryResult<TeamsListResponse, Error> {
  return useQuery({
    queryKey: teamsKeys.list(projectId, params),
    queryFn: async ({ signal }) => (await apiClient.get<TeamsListResponse>(`/projects/${projectId}/teams`, { params: toWireParams(params), signal })).data,
    enabled: projectId.length > 0,
    placeholderData: keepPreviousData,
  });
}

/** The team's detail read. `quiet` — a form's Load the latest: a team gone meanwhile answers the form, never the
 *  not-found page (ADR-084 §6). */
export async function fetchTeam(projectId: string, teamId: string, quiet = false): Promise<TeamDetail> {
  return (await apiClient.get<TeamDetail>(`/projects/${projectId}/teams/${teamId}`, quiet ? { quietNotFound: true } : undefined)).data;
}

export function useTeam(projectId: string, teamId: string): UseQueryResult<TeamDetail, Error> {
  return useQuery({
    queryKey: teamsKeys.detail(projectId, teamId),
    queryFn: () => fetchTeam(projectId, teamId),
    enabled: projectId.length > 0 && teamId.length > 0,
  });
}

async function fetchTeamNames(projectId: string, rule: TeamNamesQuery['rule'], signal?: AbortSignal): Promise<TeamNamesResponse> {
  return (await apiClient.get<TeamNamesResponse>(`/projects/${projectId}/teams/names`, { params: { rule }, signal })).data;
}

/** The teams as a picker's source (the small kind): every team of the project (the naming rule — `all`), or the ones
 *  inside the viewer's team scope (`grantable` — the scope editor's Teams section). A disabled team is an inactive row. */
export function teamNamesSource(projectId: string, rule: TeamNamesQuery['rule'] = 'all'): SmallSource<TeamNamesResponse> {
  return {
    kind: 'small',
    queryKey: teamsKeys.names(projectId, rule),
    fetch: (signal) => fetchTeamNames(projectId, rule, signal),
    rows: (r) => r.items.map((t) => ({ id: t.id, label: t.name, pickable: true, inactive: t.is_disabled, group: null })),
  };
}
